// React
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Servisni sloj
import { savePricingVersionAPI } from "services/api/pricing/api";
import { transformPricingVersionToForm } from "services/api/pricing/transformations";
import { PricingVersion, PricingVersionEditFormValues } from "services/api/pricing/types";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";

// UI
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardStepBar from "ui/components/Wizard/WizardStepBar";

// Lokalne komponente
import PricingBasicsStep from "./components/PricingBasicsStep/PricingBasicsStep";
import PricingCombinationsStep from "./components/PricingCombinationsStep/PricingCombinationsStep";
import PricingMeteredStep from "./components/PricingMeteredStep/PricingMeteredStep";
import PricingOtherValuesStep from "./components/PricingOtherValuesStep/PricingOtherValuesStep";
import PricingSummaryCard from "./components/PricingSummaryCard/PricingSummaryCard";
import PricingTiersStep from "./components/PricingTiersStep/PricingTiersStep";

// Tipovi i konstante
import { PRICING_MANDATORY_STEP_COUNT, PRICING_STEP_LABELS } from "./consts";
import { PricingEditCardProps } from "./types";
import {
  pricingBasicsStepSchema,
  PRICING_COMBINATIONS_STEP_SCHEMA,
  PRICING_TIERS_STEP_SCHEMA,
} from "./validation";

/** Datum bez vremena — usporedba ide na dan, ne na sat. */
const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Obrazac cjenika, sekcija po korak čarobnjaka.
 *
 * Spremanje je jedno, na zadnjem koraku — backend prima i provjerava cijelu verziju odjednom,
 * pa bi spremanje po koraku palo na validaciji sekcije koja još nije došla na red.
 *
 * Nacrt i verzija koja vrijedi danas mijenjaju se u mjestu; iz starije verzije spremanje
 * stvara novu, jer se ono po čemu je već obračunato ne prepisuje.
 */
const PricingEditCard: React.FC<PricingEditCardProps> = (props: PricingEditCardProps) => {
  const { show, version, packs, isEffective, onSuccess, onClose } = props;

  /**
   * Najraniji dopušten datum. Verzija koja vrijedi danas smije zadržati svoj, pa se za nju
   * granica spušta na taj dan; inače je najranije danas.
   */
  const minValidFrom = useMemo(
    () => (isEffective ? startOfDay(new Date(version.validFrom)) : startOfDay(new Date())),
    [isEffective, version.validFrom]
  );

  /** Shema koja se provjerava prije prelaska s koraka; koraci bez svojih pravila su `null`. */
  const stepSchemas = useMemo(
    () => [
      pricingBasicsStepSchema(minValidFrom),
      PRICING_COMBINATIONS_STEP_SCHEMA,
      PRICING_TIERS_STEP_SCHEMA,
      null,
      null,
    ],
    [minValidFrom]
  );

  const [formValues, setFormValues] = useState<PricingVersionEditFormValues>(
    transformPricingVersionToForm(version)
  );
  const [errors, setErrors] = useState<Errors>({});
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState<number>(0);

  // Dvoklik na „Nastavi" inače preskoči korak.
  const isProcessingRef = useRef<boolean>(false);

  // Modal ostaje montiran, pa se svako otvaranje mora vratiti na verziju s poslužitelja —
  // inače bi se vidjele nespremljene izmjene iz prošlog otvaranja.
  useEffect(() => {
    if (!show) return;

    setFormValues(transformPricingVersionToForm(version));
    setErrors({});
    setServerErrors([]);
    setStepCurrent(0);
  }, [show, version]);

  /** Ostale vrijednosti bez mjerenih — one se uređuju na svom koraku. */
  const otherValues = useMemo(() => {
    const meteredCodes = version.meteredItems.map((item) => item.code);

    return version.otherValues.filter((item) => !meteredCodes.includes(item.code));
  }, [version]);

  /*----------------------------------------------------------------
      NAVIGACIJA
  ----------------------------------------------------------------*/

  const handleNextStep = useCallback(() => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;

    try {
      const schema = stepSchemas[stepCurrent];

      if (schema && !validateYup(formValues as any, schema, setErrors)) {
        return;
      }

      setErrors({});
      setStepCurrent((step) => step + 1);
    } finally {
      isProcessingRef.current = false;
    }
  }, [formValues, stepCurrent, stepSchemas]);

  const handleBackStep = useCallback(() => {
    setStepCurrent((step) => step - 1);
  }, []);

  const handleStepClick = useCallback((stepIndex: number) => {
    setStepCurrent(stepIndex);
  }, []);

  /*----------------------------------------------------------------
      SPREMANJE
  ----------------------------------------------------------------*/

  const handleFinish = useCallback(async () => {
    setServerErrors([]);

    // Koraci se smiju preskakati klikom na traku, pa se prije slanja vrte sve sheme redom.
    // Staje na prvom koraku koji ne prolazi i tamo vraća korisnika — inače bi greška ostala
    // na sekciji koju ne gleda.
    const failingStep = stepSchemas.findIndex(
      (schema) => schema && !validateYup(formValues as any, schema, setErrors)
    );

    if (failingStep >= 0) {
      setStepCurrent(failingStep);
      return;
    }

    setIsSaving(true);

    try {
      const saved = await savePricingVersionAPI(formValues);
      onSuccess(saved);
    } catch (error: any) {
      // Tvrde brane s backenda — cijena ispod nabave, rupa u pojasevima, zamrznuta verzija.
      const list = error?.response?.data?.errors;

      if (Array.isArray(list)) {
        setServerErrors(list.map((e: any) => e.description));
      }
    } finally {
      setIsSaving(false);
    }
  }, [formValues, onSuccess, stepSchemas]);

  /*----------------------------------------------------------------
      PRIKAZ
  ----------------------------------------------------------------*/

  const steps = [
    <WizardStep key="basics" stepNumber={0} stepCurrent={stepCurrent}>
      <PricingBasicsStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        version={version}
        isEffective={isEffective}
        minValidFrom={minValidFrom}
      />
    </WizardStep>,
    <WizardStep key="combinations" stepNumber={1} stepCurrent={stepCurrent}>
      <PricingCombinationsStep
        formValues={formValues}
        setFormValues={setFormValues}
        packs={packs}
        errors={errors}
      />
    </WizardStep>,
    <WizardStep key="tiers" stepNumber={2} stepCurrent={stepCurrent}>
      <PricingTiersStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
      />
    </WizardStep>,
    <WizardStep key="metered" stepNumber={3} stepCurrent={stepCurrent}>
      <PricingMeteredStep
        formValues={formValues}
        setFormValues={setFormValues}
        items={version.meteredItems}
      />
    </WizardStep>,
    <WizardStep key="other" stepNumber={4} stepCurrent={stepCurrent}>
      <PricingOtherValuesStep
        formValues={formValues}
        setFormValues={setFormValues}
        items={otherValues}
      />
    </WizardStep>,
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="full"
      position="side"
      intro={{
        content: (
          <PricingSummaryCard formValues={formValues} packs={packs} />
        ),
      }}
    >
      <ModalHeader
        title={version.isDraft ? "Uređivanje nacrta" : "Novi cjenik"}
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />

      {/* Traka stoji izvan tijela da ne odscrola sa sadržajem koraka. */}
      <div className="flex-shrink-0">
        <WizardStepBar
          stepLabels={PRICING_STEP_LABELS}
          stepCurrent={stepCurrent}
          onStepClick={handleStepClick}
          firstStepCompleted={!!formValues.validFrom}
          mandatoryCount={PRICING_MANDATORY_STEP_COUNT}
        />
      </div>

      <ModalBody fixedFooterSpacing={false}>
        {/* Greška s backenda stigne na zadnjem koraku, ali ostaje vidljiva i ako se vratiš. */}
        {serverErrors.length > 0 && (
          <div className="m-4 rounded-md border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-700 mb-1">Cjenik nije spremljen</p>
            <ul className="list-disc pl-5 text-sm text-red-700">
              {serverErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        <Wizard steps={steps} />
      </ModalBody>

      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isSaving}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default PricingEditCard;
